import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { buyPackage } from "@/lib/buyPackage";
import Seo from "@/components/Seo";
import { useConsultModal } from "@/components/ConsultModal";
import { ArrowRight, ClockClockwise, Sparkle, Brain, Heart, Stack, Lifebuoy, VideoCamera } from "@phosphor-icons/react";
import PageLoader from "@/components/PageLoader";

const categoryIcon = {
  Therapy: Brain,
  Assessment: Stack,
  Programme: Sparkle,
  Retainer: Lifebuoy,
  Wellbeing: Heart,
};

export default function CouchPricing() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gating, setGating] = useState({ unlocked: false, gated_slugs: ["making-sense-of-yourself", "become-your-own-brain-manager"] });
  const { format } = useCurrency();
  const { open: openConsult } = useConsultModal();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState("");

  const handleBuy = async (s) => {
    setBuying(s.slug);
    await buyPackage({ id: s.id, name: s.name, price: s.price_inr, user, navigate });
    setBuying("");
  };

  useEffect(() => {
    api.get("/services/public").then((r) => setServices(r.data)).finally(() => setLoading(false));
    api.get("/services/gating").then((r) => setGating(r.data)).catch(() => {});
  }, []);

  return (
    <Page>
      <Seo title="Couch · Sessions & Pricing" description="Transparent pricing for MindDhara Couch sessions, assessments, programmes, retainers and standalone wellbeing sessions." />

      <section className="md-section md-page-hero">
        <div className="md-container">
          <div className="md-label-eyebrow mb-4">Couch · Sessions &amp; Pricing</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif">
            One ladder of care, <em>priced openly</em>
          </h1>
          <p className="text-base mt-5 max-w-2xl mx-auto">
            Every journey begins with understanding. Through assessment, formulation, therapy, rehabilitation, and ongoing support, we help you make sense of your experiences and develop the tools to navigate life with greater clarity, resilience, and ease.
          </p>
        </div>
      </section>

      <section className="md-section">
        <div className="md-container">
        {loading ? <PageLoader sm /> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {services.map((s) => {
              const Icon = categoryIcon[s.category] || Brain;
              const locked = !gating.unlocked && (gating.gated_slugs || []).includes(s.slug);
              return (
                <article key={s.id} className="md-card md-card-hover flex flex-col" data-testid={`service-card-${s.slug}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="how-glass-ico" style={{ background: "var(--md-bg-alt)", borderColor: "var(--md-border)" }}>
                      <Icon size={26} weight="duotone" style={{ color: "var(--md-primary)" }} />
                    </div>
                  </div>

                  <div className="md-label-eyebrow mb-2" style={{ color: "var(--md-secondary-dark)" }}>{s.category}</div>
                  <h3 className="text-2xl font-serif mb-1" style={{ color: "var(--md-primary)" }}>{s.name}</h3>
                  {s.subtitle && <p className="text-sm italic" style={{ color: "var(--md-text-muted)" }}>{s.subtitle}</p>}

                  <p className="mt-4 text-sm flex-1" style={{ color: "var(--md-text-soft)" }}>{s.description}</p>

                  <div className="flex flex-wrap items-center gap-2 mt-5">
                    <span className="svc-meta-chip"><ClockClockwise size={13} weight="duotone" /> {s.duration}</span>
                    {s.format && <span className="svc-meta-chip"><VideoCamera size={13} weight="duotone" /> {s.format}</span>}
                  </div>

                  {s.eligibility && (
                    <p className="mt-3 text-xs px-3 py-2 rounded" style={{ background: "var(--md-bg-alt)", color: "var(--md-text-soft)" }} data-testid={`service-eligibility-${s.slug}`}>
                      {s.eligibility}
                    </p>
                  )}

                  <div className="flex items-end justify-between mt-6 pt-5 border-t" style={{ borderColor: "var(--md-border)" }}>
                    <div>
                      {s.price_inr ? (
                        <>
                          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--md-text-muted)" }}>From</div>
                          <div className="text-3xl font-serif" style={{ color: "var(--md-primary)" }} data-testid={`service-price-${s.slug}`}>{format(s.price_inr)}</div>
                        </>
                      ) : (
                        <>
                          <div className="text-xs uppercase tracking-wider" style={{ color: "var(--md-text-muted)" }}>Pricing</div>
                          <div className="text-lg font-serif" style={{ color: "var(--md-primary)" }} data-testid={`service-price-${s.slug}`}>{s.price_note || "On request"}</div>
                        </>
                      )}
                    </div>

                    {locked ? (
                      <button type="button" disabled className="btn-primary text-sm svc-cta-locked" title="Unlock by booking Knowing You first, or with a valid referral" data-testid={`service-cta-${s.slug}`}>
                        Book &ldquo;Knowing You&rdquo; first
                      </button>
                    ) : s.founder_delivered ? (
                      <button type="button" onClick={openConsult} className="btn-primary text-sm" data-testid={`service-cta-${s.slug}`}>
                        Talk to Shreeja <ArrowRight size={14} weight="bold" />
                      </button>
                    ) : s.price_inr ? (
                      <button type="button" onClick={() => handleBuy(s)} disabled={buying === s.slug} className="btn-primary text-sm" data-testid={`service-cta-${s.slug}`}>
                        {buying === s.slug ? "Opening…" : (<>Buy Now <ArrowRight size={14} weight="bold" /></>)}
                      </button>
                    ) : (
                      <Link to={`/therapists?source=couch-pricing-${s.slug}`} onClick={() => sessionStorage.setItem("md_service_slug", s.slug)} className="btn-primary text-sm" data-testid={`service-cta-${s.slug}`}>
                        Book <ArrowRight size={14} weight="bold" />
                      </Link>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </div>
      </section>
    </Page>
  );
}
